import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useInterviewSession } from '../hooks/useInterviewSession';
import { setCandidateToken } from '../lib/interviewApi';
import InterviewIntro from '../components/interview/InterviewIntro';
import InterviewQuestion from '../components/interview/InterviewQuestion';
import InterviewComplete from '../components/interview/InterviewComplete';

// Candidate-facing page for sessions that were created by Frappe HRMS via
// the /from-frappe webhook (no /start call needed). The interview_id comes
// straight from the URL, e.g. /interview/by-id/int_4d1e380da24e?token=...
export default function InterviewResumePage() {
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    phase,
    session,
    currentQuestion,
    result,
    error,
    resume,
    loadNextQuestion,
    submitAnswer,
  } = useInterviewSession();

  useEffect(() => {
    // Token comes from the share link Frappe writes to duty_ai_candidate_url.
    // Pinning it before resume() ensures every API call from this page
    // attaches the Bearer header.
    setCandidateToken(searchParams.get('token'));
  }, [searchParams]);

  useEffect(() => {
    if (phase === 'idle' && interviewId) {
      resume(interviewId);
    }
  }, [phase, interviewId, resume]);

  return (
    <div className="min-h-screen w-full bg-white py-10 px-4">
      <div className="max-w-2xl mx-auto">
      {phase === 'loading' && (
        <div className="text-center py-20 text-[#1b5e20]">
          <div className="animate-pulse text-lg font-medium">Loading your interview...</div>
        </div>
      )}

      {phase === 'error' && (
        <div className="max-w-xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg">
          <h2 className="font-bold text-red-700 mb-2">Something went wrong</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
          >
            Go to Home
          </button>
        </div>
      )}

      {phase === 'intro' && <InterviewIntro session={session} onBegin={loadNextQuestion} />}

      {(phase === 'question' || phase === 'submitting') && currentQuestion && (
        <InterviewQuestion
          question={currentQuestion}
          session={session}
          onSubmit={submitAnswer}
          submitting={phase === 'submitting'}
        />
      )}

      {phase === 'completing' && (
        <div className="text-center py-20 text-[#1b5e20]">
          <div className="animate-pulse text-lg font-medium">
            Generating your evaluation report...
          </div>
          <div className="text-sm text-gray-500 mt-2">This may take up to 30 seconds.</div>
        </div>
      )}

      {phase === 'complete' && <InterviewComplete result={result} />}
      </div>
    </div>
  );
}
